import { selectFeatureFlags } from 'core/userContext';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { FlexSpacer } from 'ui/flexSpacer';
import { FormField } from 'ui/formField';
import { RadioGroup } from 'ui/radioGroup/radioGroup';
import { HMACFormField } from 'modules/projectSettings/components/ProjectSettingsForm/HMACFormField';
import { CONTENT_FIELD_NAMES } from '../../constants';
import { LocalesList } from '../LocalesList';
import { ConnectedFormField, MarkdownDescription, OptionalLabelText } from './ConnectedFormField';
import { DescribedCheckbox } from './DescribedCheckbox';
import { FormSection } from './FormSection';

const marketingChannelsOptions = [
  {
    id: 'sendMarketing',
    label: 'Send the default localization of the campaign',
    value: 'sendMarketing',
  },
  {
    id: 'dontSendMarketing',
    label: "Don't send the campaign to the user",
    value: 'dontSendMarketing',
  },
];

const transactionalChannelsOptions = [
  {
    id: 'sendTransactional',
    label: 'Send the default localization of the campaign',
    value: 'sendTransactional',
  },
  {
    id: 'dontSendTransactional',
    label: "Don't send the campaign to the user",
    value: 'dontSendTransactional',
  },
];

const {
  OVERRIDE_CONTENT_NOT_FOUND_MARKETING,
  OVERRIDE_CONTENT_NOT_FOUND_TRANSACTIONAL,
  TEMPLATE_DEFAULT_LOCALE_MODIFIABLE,
  WYSIWYG_EDITOR_MODE,
} = CONTENT_FIELD_NAMES;

export const CONTENT_FORM_SECTION_TEXT = {
  sectionHeadingText: 'Content',
  sectionId: 'content',
  /** WYSIWYG */
  wysiwygEditorModeDescriptionText:
    'Add the WYSIWYG editor as an option for new templates. You can also switch between the WYSIWYG and Side by Side editors.',
  wysiwygEditorModeDescriptionTextId: 'wysiwygEditorModeDescriptionText',
  wysiwygEditorModeLabelText: 'Enable WYSIWYG editor',
  wysiwygEditorModeSupportUrl:
    'https://support.iterable.com/hc/en-us/articles/206382645-Project-Settings-Overview-#use-wysiwyg-template-editor',
  /** HMAC secret */
  hmacSecretDescriptionText:
    'Set a secret used with the hmacSHA1 handlebars function to generate authentication tokens.',
  hmacSecretDescriptionTextId: 'hmacSecretDescriptionText',
  hmacSecretLabelText: 'HMAC secret',
  hmacSecretSupportUrl: 'https://support.iterable.com/hc/articles/209732326',
  /** Locales */
  localesLabelText: 'Locales',
  localesDescriptionText:
    'Set up locale codes that you can use to deliver content to users according to their region and preferred language.',
  /** template default locale modifiable */
  templateDefaultLocaleModifiableDescriptionText:
    'Set a default locale `{locale}` for every new template and associated campaign.',
  templateDefaultLocaleModifiableDescriptionTextId:
    'templateDefaultLocaleModifiableDescriptionText',
  templateDefaultLocaleModifiableLabelText: 'Set a locale default on a per-template basis',
  templateDefaultLocaleModifiableSupportUrl:
    'https://support.iterable.com/hc/en-us/articles/12441621049876-Setting-Up-Locales-For-Your-Project-#choosing-a-default-locale-for-your-project',
  /** locale override */
  overrideContentNotFoundDescriptionText:
    "Send campaigns using the project's default locale when no localized template version exists for the user's locale.",
  overrideContentNotFoundLabelText: 'Locale preferences',
  overrideContentNotFoundMarketingLabelText: 'Marketing channels',
  overrideContentNotFoundTransactionalLabelText: 'Transactional channels',
};

export const ContentFormSection = () => {
  const { LocaleImprovements } = useSelector(selectFeatureFlags);

  return (
    <FormSection
      id={CONTENT_FORM_SECTION_TEXT.sectionId}
      data-test={`${CONTENT_FORM_SECTION_TEXT.sectionId}-form-section`}
      headingText={CONTENT_FORM_SECTION_TEXT.sectionHeadingText}
    >
      {/* WYSIWYG Editor */}
      <ConnectedFormField name={WYSIWYG_EDITOR_MODE}>
        <DescribedCheckbox
          label={CONTENT_FORM_SECTION_TEXT.wysiwygEditorModeLabelText}
          DescriptionText={
            <MarkdownDescription
              id={CONTENT_FORM_SECTION_TEXT.wysiwygEditorModeDescriptionTextId}
              markdownText={CONTENT_FORM_SECTION_TEXT.wysiwygEditorModeDescriptionText}
              supportUrl={CONTENT_FORM_SECTION_TEXT.wysiwygEditorModeSupportUrl}
            />
          }
          name={WYSIWYG_EDITOR_MODE}
        />
      </ConnectedFormField>
      {/* HMAC Secret */}
      <HMACFormField
        fieldDescriptionText={
          <MarkdownDescription
            id={CONTENT_FORM_SECTION_TEXT.hmacSecretDescriptionTextId}
            markdownText={CONTENT_FORM_SECTION_TEXT.hmacSecretDescriptionText}
            supportUrl={CONTENT_FORM_SECTION_TEXT.hmacSecretSupportUrl}
          />
        }
        fieldLabelText={<OptionalLabelText text={CONTENT_FORM_SECTION_TEXT.hmacSecretLabelText} />}
      />
      {/* Locales */}
      <FormField
        labelText={CONTENT_FORM_SECTION_TEXT.localesLabelText}
        descriptionText={CONTENT_FORM_SECTION_TEXT.localesDescriptionText}
      >
        <LocalesList />
      </FormField>
      {/* Template default locale modifiable */}
      {LocaleImprovements && (
        <ConnectedFormField name={TEMPLATE_DEFAULT_LOCALE_MODIFIABLE}>
          <DescribedCheckbox
            label={CONTENT_FORM_SECTION_TEXT.templateDefaultLocaleModifiableLabelText}
            DescriptionText={
              <MarkdownDescription
                id={CONTENT_FORM_SECTION_TEXT.templateDefaultLocaleModifiableDescriptionTextId}
                markdownText={
                  CONTENT_FORM_SECTION_TEXT.templateDefaultLocaleModifiableDescriptionText
                }
                supportUrl={CONTENT_FORM_SECTION_TEXT.templateDefaultLocaleModifiableSupportUrl}
              />
            }
            name={TEMPLATE_DEFAULT_LOCALE_MODIFIABLE}
          />
        </ConnectedFormField>
      )}
      {/* Locale override */}
      <FormField
        descriptionText={CONTENT_FORM_SECTION_TEXT.overrideContentNotFoundDescriptionText}
        labelText={CONTENT_FORM_SECTION_TEXT.overrideContentNotFoundLabelText}
      >
        <FlexSpacer alignItems="stretch" flexDirection="column" gap="base">
          <Controller
            name={OVERRIDE_CONTENT_NOT_FOUND_MARKETING}
            render={({ field }) => {
              const handleSelect = (value: string) => {
                return field.onChange(value === 'sendMarketing');
              };
              return (
                <RadioGroup
                  label={CONTENT_FORM_SECTION_TEXT.overrideContentNotFoundMarketingLabelText}
                  name={OVERRIDE_CONTENT_NOT_FOUND_MARKETING}
                  onSelect={handleSelect}
                  options={marketingChannelsOptions}
                  radioAlignment="flex-start"
                  value={!!field.value ? 'sendMarketing' : 'dontSendMarketing'}
                />
              );
            }}
          />
          <Controller
            name={OVERRIDE_CONTENT_NOT_FOUND_TRANSACTIONAL}
            render={({ field }) => {
              const handleSelect = (value: string) => {
                return field.onChange(value === 'sendTransactional');
              };
              return (
                <RadioGroup
                  label={CONTENT_FORM_SECTION_TEXT.overrideContentNotFoundTransactionalLabelText}
                  name={OVERRIDE_CONTENT_NOT_FOUND_TRANSACTIONAL}
                  onSelect={handleSelect}
                  options={transactionalChannelsOptions}
                  radioAlignment="flex-start"
                  value={!!field.value ? 'sendTransactional' : 'dontSendTransactional'}
                />
              );
            }}
          />
        </FlexSpacer>
      </FormField>
    </FormSection>
  );
};
